import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
// FP3-035F Issue 2 — Preserve ?as_provider= across internal navigation.
import useShadowNavigate from "@/lib/useShadowNavigate";
// FP3-035F Issue 2 — Notifications is a heavy mutation surface
// (teacher notifications, unavailability send, weekly updates,
// settings). In read-only shadow mode the entire page is wrapped in
// a `<fieldset disabled>` so every interactive child becomes a
// passive read-only view.
// FP3-035F correction (Issue 1) — In addition, hard-redirect any
// direct admin nav into Notifications back to the provider's
// read-only Today page so they never see the provider's private
// notifications outbox / unavailability log.
import { useReadOnlyAsProvider } from "@/components/layout/ReadOnlyProviderBanner";
import PageShell from "@/components/layout/PageShell";
import { Bell, Eye, PaperPlaneTilt, ChatCircleText, Gear, ClockCounterClockwise, Check, X, ArrowClockwise, CalendarBlank, Trash, ArrowLeft, FloppyDisk, ListChecks, Warning } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api from "@/lib/api";

// Compute "tomorrow" relative to the user's local timezone (NOT UTC) so
// the default target_date matches what the user sees on the calendar.
function localTomorrowISO() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDatePretty(iso) {
  if (!iso) return "";
  // Parse as local Date (YYYY-MM-DD → local midnight, not UTC midnight)
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const d = m ? new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10)) : new Date(iso);
  return d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
}

export default function Notifications() {
  const navigate = useShadowNavigate();
  const readOnly = useReadOnlyAsProvider();
  const [provider, setProvider] = useState(null);
  const [settings, setSettings] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [unavailPeriods, setUnavailPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  // Tab state synced to ?tab=… so deep links from Today (Mark Unavailable button)
  // open the right tab on first paint.
  const initialTab = searchParams.get("tab") || "reminders";
  const [tab, setTab] = useState(
    ["reminders", "weekly", "unavailable", "teachers", "settings", "history"].includes(initialTab)
      ? initialTab
      : "reminders"
  );

  useEffect(() => {
    (async () => {
      try {
        const p = (await api.getCurrentProvider()).data;
        setProvider(p);
        const [s, t, l, u] = await Promise.all([
          api.getNotificationSettings({ provider_id: p.id }),
          api.listNotificationTeachers(),
          api.listNotificationLogs({ provider_id: p.id }),
          api.listProviderUnavailability({ provider_id: p.id }),
        ]);
        setSettings(s.data);
        setTeachers(t.data || []);
        setLogs(l.data || []);
        setUnavailPeriods(u.data || []);
      } catch (e) {
        toast.error("Failed to load notifications data");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const refreshLogs = async () => {
    if (!provider) return;
    const l = await api.listNotificationLogs({ provider_id: provider.id });
    setLogs(l.data || []);
  };
  const refreshUnavail = async () => {
    if (!provider) return;
    const u = await api.listProviderUnavailability({ provider_id: provider.id });
    setUnavailPeriods(u.data || []);
  };

  if (loading) {
    return <div className="p-8 text-sm text-[#6B7280]">Loading notifications…</div>;
  }

  // FP3-035F correction (Issue 1) — Admin shadowing the provider
  // must not see Notifications at all. Redirect any direct nav
  // (manual URL, stale link) back to the provider's read-only
  // Today page with the shadow flag intact. Placed AFTER hooks so
  // the hook order stays stable across renders.
  if (readOnly.active) {
    return (
      <Navigate
        to={`/?as_provider=${readOnly.providerId}`}
        replace
      />
    );
  }

  return (
    <PageShell testId="notifications-page" variant="compact" className="animate-none">
      <header className="mb-6" data-testid="notifications-header">
        {/* FP3-112F — Title + description on top, Back-to-Today
            action DIRECTLY BELOW (natural left-aligned). Dropped
            `justify-between` so Back-to-Today stops floating at the
            far-right edge of the 1280px shell. */}
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#E8F0EB] flex items-center justify-center">
            <Bell size={20} weight="duotone" className="text-[#1E3F33]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#1F2937] heading-font">Notifications</h1>
            <p className="text-[13px] text-[#6B7280]">Teacher communication · grouped by recipient · mock-only send for now</p>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="notifications-header-actions">
          <Button
            data-testid="notifications-back"
            variant="outline"
            size="sm"
            onClick={() => navigate("/")}
            className="border-[#E5E3DB] text-[#1F2937] hover:bg-[#F3F1EC]"
          >
            <ArrowLeft size={14} className="mr-1.5" /> Back to Today
          </Button>
        </div>
      </header>

      {/* FP3-035F Issue 2 — Wrap each tab content in a disabled
          fieldset when admin is shadowing the provider, so buttons /
          inputs / textareas / selects inside don't fire. The
          TabsList stays outside the fieldset so tab navigation
          continues to work in read-only mode. */}
      <Tabs value={tab} onValueChange={(v) => { setTab(v); setSearchParams(v === "reminders" ? {} : { tab: v }); }} className="space-y-4">
        <TabsList className="bg-[#F3F1EC]">
          <TabsTrigger value="reminders" data-testid="tab-reminders">
            <ChatCircleText size={14} className="mr-1.5" /> Reminders
          </TabsTrigger>
          <TabsTrigger value="weekly" data-testid="tab-weekly">
            <ListChecks size={14} className="mr-1.5" /> Weekly updates
          </TabsTrigger>
          <TabsTrigger value="unavailable" data-testid="tab-unavailable">
            <CalendarBlank size={14} className="mr-1.5" /> Unavailability ({unavailPeriods.length})
          </TabsTrigger>
          <TabsTrigger value="teachers" data-testid="tab-teachers">
            <Bell size={14} className="mr-1.5" /> Teachers ({teachers.length})
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">
            <Gear size={14} className="mr-1.5" /> Settings
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <ClockCounterClockwise size={14} className="mr-1.5" /> History ({logs.length})
          </TabsTrigger>
        </TabsList>
        <fieldset disabled={readOnly.active} className="contents" data-testid="notifications-readonly-fieldset">

        <TabsContent value="reminders">
          <RemindersTab provider={provider} settings={settings} onLogsRefresh={refreshLogs} />
        </TabsContent>
        <TabsContent value="weekly">
          <WeeklyUpdatesTab provider={provider} settings={settings} onLogsRefresh={refreshLogs} />
        </TabsContent>
        <TabsContent value="unavailable">
          <UnavailableTab
            provider={provider}
            settings={settings}
            onSettingsSaved={(s) => setSettings(s)}
            periods={unavailPeriods}
            onCommitted={async () => {
              await Promise.all([refreshLogs(), refreshUnavail()]);
              setTab("history");
              setSearchParams({ tab: "history" });
            }}
            onDeleted={refreshUnavail}
          />
        </TabsContent>
        <TabsContent value="teachers">
          <TeachersTab teachers={teachers} onUpdate={async (email, payload) => {
            await api.updateNotificationTeacher(email, payload);
            const t = await api.listNotificationTeachers();
            setTeachers(t.data || []);
            toast.success("Teacher updated");
          }} />
        </TabsContent>
        <TabsContent value="settings">
          <SettingsTab provider={provider} settings={settings} onSave={async (payload) => {
            const r = await api.updateNotificationSettings({ provider_id: provider.id }, payload);
            setSettings(r.data);
            toast.success("Settings saved");
          }} />
        </TabsContent>
        <TabsContent value="history">
          <HistoryTab logs={logs} />
        </TabsContent>
        </fieldset>
      </Tabs>
    </PageShell>
  );
}


// ── Reminders tab ──────────────────────────────────────────────────────────
function RemindersTab({ provider, settings, onLogsRefresh }) {
  const [targetDate, setTargetDate] = useState(localTomorrowISO());
  const [preview, setPreview] = useState(null);
  const [building, setBuilding] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);

  const generate = async () => {
    setBuilding(true);
    try {
      const r = await api.buildNotificationPreview({
        type: "tomorrow_reminder",
        provider_id: provider.id,
        target_date: targetDate,
      });
      setPreview(r.data);
      if ((r.data.items || []).length === 0) {
        toast.info("No teachers to notify for that day", {
          description: "No students with `teacher_email` set are scheduled.",
        });
      }
    } catch (e) {
      toast.error("Preview build failed", { description: e.message });
    } finally {
      setBuilding(false);
    }
  };

  const sendAll = async () => {
    if (!preview || !preview.items?.length) return;
    setSending(true);
    try {
      const r = await api.sendNotifications({
        type: preview.type,
        provider_id: provider.id,
        target_date: preview.target_date,
        items: preview.items,
      });
      toast.success(`Sent ${r.data.sent} ${r.data.sent === 1 ? "email" : "emails"}` +
        (r.data.skipped ? ` · ${r.data.skipped} skipped (notifications off)` : ""), {
        description: "Mock-only send — logged to history.",
      });
      await onLogsRefresh();
      setPreview(null);
    } catch (e) {
      toast.error("Send failed", { description: e.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="border-[#E5E3DB]">
      <CardHeader>
        <CardTitle className="text-base text-[#1F2937] heading-font">Tomorrow's schedule reminder</CardTitle>
        <p className="text-xs text-[#6B7280]">
          Build one combined email per teacher listing their students you have scheduled on the target day.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-[11px] text-[#6B7280] uppercase tracking-wider">Target date</Label>
            <Input
              data-testid="reminder-target-date"
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              className="w-44 mt-1"
            />
          </div>
          <Button
            data-testid="reminder-build-preview"
            onClick={generate}
            disabled={building}
            className="bg-[#1E3F33] hover:bg-[#173127] text-white"
          >
            <Eye size={14} className="mr-1.5" />
            {building ? "Building…" : "Generate preview"}
          </Button>
          {!settings?.enabled && (
            <span className="text-[11px] text-[#9C6B2E] italic">
              Notifications are disabled in Settings — send is still allowed for testing while in mock mode.
            </span>
          )}
        </div>

        {preview && (
          <div className="border border-[#E5E3DB] rounded-lg bg-[#FAFAF7] p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-[#1F2937]">
                  {preview.stats.teachers_count} teacher{preview.stats.teachers_count === 1 ? "" : "s"} · {preview.stats.students_count} student{preview.stats.students_count === 1 ? "" : "s"}
                </p>
                <p className="text-[11px] text-[#6B7280]">
                  Target day: <span className="font-medium">{preview.target_date_pretty}</span> · Sender: {preview.provider_name}
                </p>
              </div>
              <Button
                data-testid="reminder-send-all"
                onClick={sendAll}
                disabled={sending || !preview.items.length}
                className="bg-[#1E3F33] hover:bg-[#173127] text-white"
              >
                <PaperPlaneTilt size={14} className="mr-1.5" />
                {sending ? "Sending…" : `Send ${preview.items.length} email${preview.items.length === 1 ? "" : "s"} (mock)`}
              </Button>
            </div>
            <Separator className="bg-[#E5E3DB] my-3" />
            <div className="space-y-2">
              {preview.items.length === 0 ? (
                <p className="text-[12px] text-[#6B7280] italic py-2">
                  No teachers with students scheduled for that day. Make sure students have `teacher_email` set on their profile.
                </p>
              ) : (
                preview.items.map((it) => (
                  <div
                    key={it.teacher_email}
                    data-testid={`reminder-item-${it.teacher_email}`}
                    className="flex items-center justify-between gap-3 bg-white border border-[#E5E3DB] rounded-md px-3 py-2"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-[#1F2937] truncate">{it.teacher_name}</p>
                        {!it.notification_enabled && (
                          <Badge variant="outline" className="text-[9px] bg-[#FBEFEC] text-[#B85C5C] border-[#B85C5C]/30">
                            disabled
                          </Badge>
                        )}
                      </div>
                      <p className="text-[11px] text-[#6B7280] truncate">
                        {it.recipient_address} · {it.students_summary.length} student{it.students_summary.length === 1 ? "" : "s"}
                        {": " + it.students_summary.map((s) => s.student_name).join(", ")}
                      </p>
                    </div>
                    <Button
                      data-testid={`reminder-preview-${it.teacher_email}`}
                      variant="outline"
                      size="sm"
                      onClick={() => setPreviewItem(it)}
                      className="text-[11px] h-7 border-[#E5E3DB]"
                    >
                      <Eye size={12} className="mr-1" /> Preview
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={!!previewItem} onOpenChange={(o) => !o && setPreviewItem(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-base">Email preview</DialogTitle>
          </DialogHeader>
          {previewItem && (
            <div className="space-y-3">
              <div className="grid grid-cols-[80px_1fr] gap-1 text-[12px]">
                <span className="text-[#6B7280] uppercase tracking-wider text-[10px]">To</span>
                <span className="text-[#1F2937]">{previewItem.recipient_address}</span>
                <span className="text-[#6B7280] uppercase tracking-wider text-[10px]">Subject</span>
                <span className="text-[#1F2937] font-medium">{previewItem.subject}</span>
              </div>
              <div className="border border-[#E5E3DB] rounded-md overflow-hidden bg-[#F5F4EE]">
                <iframe
                  data-testid="email-preview-iframe"
                  title="Email preview"
                  srcDoc={previewItem.html}
                  sandbox=""
                  className="w-full h-[480px] bg-white"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewItem(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// ── Weekly updates tab ─────────────────────────────────────────────────────
// Iter 36 — Two new notification flows share this tab:
//   • no_session_week     — students with obligations who have 0 sessions
//   • schedule_change_week — sessions disrupted by unavailability or closures
// Both follow the same preview → mock send → log pattern as RemindersTab and
// land in the existing History tab.
function localMondayISO() {
  const d = new Date();
  const dow = d.getDay(); // 0=Sun..6=Sat
  const offset = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function localFridayISO() {
  const d = new Date();
  const dow = d.getDay();
  const offset = dow === 0 ? -2 : 5 - dow;
  d.setDate(d.getDate() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const WEEKLY_KIND_META = {
  no_session_week: {
    title: "No-service heads-up",
    desc: "Students who have a service obligation but zero sessions on your calendar this week. One combined email per teacher, listing only their roster.",
    accent: "#3F4756",
    accentLight: "#EDF2F7",
    badge: "No service",
    icon: ListChecks,
  },
  schedule_change_week: {
    title: "Schedule changes",
    desc: "Sessions that won't happen as usual this week — provider unavailability, school closures, district holidays. Grouped by teacher with a per-row reason.",
    accent: "#9C6B2E",
    accentLight: "#FBEFEC",
    badge: "Changes",
    icon: Warning,
  },
};

function WeeklyUpdatesTab({ provider, settings, onLogsRefresh }) {
  const [kind, setKind] = useState("no_session_week");
  const [weekStart, setWeekStart] = useState(localMondayISO());
  const [weekEnd, setWeekEnd] = useState(localFridayISO());
  const [preview, setPreview] = useState(null);
  const [building, setBuilding] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);

  const meta = WEEKLY_KIND_META[kind];
  const Icon = meta.icon;

  const generate = async () => {
    setBuilding(true);
    setPreview(null);
    try {
      const r = await api.buildNotificationPreview({
        type: kind,
        provider_id: provider.id,
        week_start: weekStart,
        week_end: weekEnd,
      });
      setPreview(r.data);
      if ((r.data.items || []).length === 0) {
        toast.info(r.data.empty_msg || "Nothing to notify for this window.");
      }
    } catch (e) {
      toast.error("Preview build failed", { description: e.message });
    } finally {
      setBuilding(false);
    }
  };

  const sendAll = async () => {
    if (!preview || !preview.items?.length) return;
    setSending(true);
    try {
      const r = await api.sendNotifications({
        type: preview.type,
        provider_id: provider.id,
        target_date: preview.week_start,
        items: preview.items,
      });
      toast.success(`Sent ${r.data.sent} ${r.data.sent === 1 ? "email" : "emails"}` +
        (r.data.skipped ? ` · ${r.data.skipped} skipped (notifications off)` : ""), {
        description: "Mock-only send — logged to history.",
      });
      await onLogsRefresh();
      setPreview(null);
    } catch (e) {
      toast.error("Send failed", { description: e.message });
    } finally {
      setSending(false);
    }
  };

  const switchKind = (next) => {
    setKind(next);
    setPreview(null);
  };

  return (
    <Card className="border-[#E5E3DB]">
      <CardHeader>
        <CardTitle className="text-base text-[#1F2937] heading-font flex items-center gap-2">
          <Icon size={18} weight="duotone" style={{ color: meta.accent }} />
          Weekly updates
        </CardTitle>
        <p className="text-xs text-[#6B7280]">
          Two flavors — flip between them and re-build the preview. Same window picker, different lens.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Kind switch */}
        <div className="flex gap-2 flex-wrap" data-testid="weekly-kind-toggle">
          {Object.entries(WEEKLY_KIND_META).map(([k, m]) => {
            const active = k === kind;
            return (
              <button
                key={k}
                data-testid={`weekly-kind-${k}`}
                onClick={() => switchKind(k)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  active ? "text-white" : "bg-[#F3F1EC] text-[#4B5563] hover:bg-[#E8E5DD]"
                }`}
                style={active ? { backgroundColor: m.accent } : {}}
              >
                <m.icon size={13} weight={active ? "fill" : "duotone"} />
                {m.title}
              </button>
            );
          })}
        </div>

        {/* Description */}
        <div className="rounded-md p-3 text-[12px] text-[#4B5563] leading-relaxed" style={{ backgroundColor: meta.accentLight }}>
          {meta.desc}
        </div>

        {/* Window picker */}
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-[11px] text-[#6B7280] uppercase tracking-wider">Week start (Mon)</Label>
            <Input
              data-testid="weekly-week-start"
              type="date"
              value={weekStart}
              onChange={(e) => setWeekStart(e.target.value)}
              className="w-44 mt-1"
            />
          </div>
          <div>
            <Label className="text-[11px] text-[#6B7280] uppercase tracking-wider">Week end (Fri)</Label>
            <Input
              data-testid="weekly-week-end"
              type="date"
              value={weekEnd}
              onChange={(e) => setWeekEnd(e.target.value)}
              className="w-44 mt-1"
            />
          </div>
          <Button
            data-testid="weekly-build-preview"
            onClick={generate}
            disabled={building || !provider}
            className="bg-[#1E3F33] hover:bg-[#173127] text-white"
          >
            <Eye size={14} className="mr-1.5" />
            {building ? "Building…" : "Generate preview"}
          </Button>
          {!settings?.enabled && (
            <span className="text-[11px] text-[#9C6B2E] italic">
              Notifications disabled in Settings — send still allowed for testing while in mock mode.
            </span>
          )}
        </div>

        {/* Preview */}
        {preview && (
          <div className="border border-[#E5E3DB] rounded-lg bg-[#FAFAF7] p-4" data-testid="weekly-preview">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2">
                  <Badge style={{ backgroundColor: meta.accent, color: "white" }}>{meta.badge}</Badge>
                  <p className="text-sm font-semibold text-[#1F2937]">
                    {preview.stats.teachers_count} teacher{preview.stats.teachers_count === 1 ? "" : "s"} · {preview.stats.affected_count}{" "}
                    {kind === "no_session_week" ? "students missing service" : "session disruptions"}
                  </p>
                </div>
                <p className="text-[11px] text-[#6B7280] mt-1">
                  Window: <span className="font-medium">{preview.range_pretty}</span> · Sender: {preview.provider_name}
                </p>
              </div>
              <Button
                data-testid="weekly-send-all"
                onClick={sendAll}
                disabled={sending || !preview.items.length}
                className="bg-[#1E3F33] hover:bg-[#173127] text-white"
              >
                <PaperPlaneTilt size={14} className="mr-1.5" />
                {sending ? "Sending…" : `Send ${preview.items.length} email${preview.items.length === 1 ? "" : "s"} (mock)`}
              </Button>
            </div>
            <Separator className="bg-[#E5E3DB] my-3" />
            <div className="space-y-2">
              {preview.items.length === 0 ? (
                <p className="text-[12px] text-[#6B7280] italic py-2">{preview.empty_msg || "Nothing to send for this window."}</p>
              ) : (
                preview.items.map((it) => (
                  <div
                    key={it.teacher_email}
                    data-testid={`weekly-item-${it.teacher_email}`}
                    className="flex items-center justify-between gap-3 bg-white border border-[#E5E3DB] rounded-md px-3 py-2"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-[#1F2937] truncate">{it.teacher_name}</p>
                        {!it.notification_enabled && (
                          <Badge variant="outline" className="text-[9px] bg-[#FBEFEC] text-[#B85C5C] border-[#B85C5C]/30">
                            disabled
                          </Badge>
                        )}
                      </div>
                      <p className="text-[11px] text-[#6B7280] truncate">
                        {it.recipient_address} · {it.students_summary.length}{" "}
                        {kind === "no_session_week"
                          ? `student${it.students_summary.length === 1 ? "" : "s"} flagged`
                          : `disruption${it.students_summary.length === 1 ? "" : "s"}`}
                      </p>
                    </div>
                    <Button
                      data-testid={`weekly-preview-${it.teacher_email}`}
                      variant="outline"
                      size="sm"
                      onClick={() => setPreviewItem(it)}
                      className="text-[11px] h-7 border-[#E5E3DB]"
                    >
                      <Eye size={12} className="mr-1" /> Preview
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={!!previewItem} onOpenChange={(o) => !o && setPreviewItem(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-base">Email preview</DialogTitle>
          </DialogHeader>
          {previewItem && (
            <div className="space-y-3">
              <div className="grid grid-cols-[80px_1fr] gap-1 text-[12px]">
                <span className="text-[#6B7280] uppercase tracking-wider text-[10px]">To</span>
                <span className="text-[#1F2937]">{previewItem.recipient_address}</span>
                <span className="text-[#6B7280] uppercase tracking-wider text-[10px]">Subject</span>
                <span className="text-[#1F2937] font-medium">{previewItem.subject}</span>
              </div>
              <div className="border border-[#E5E3DB] rounded-md overflow-hidden bg-[#F5F4EE]">
                <iframe
                  data-testid="weekly-email-iframe"
                  title="Email preview"
                  srcDoc={previewItem.html}
                  sandbox=""
                  className="w-full h-[480px] bg-white"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewItem(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// ── Teachers tab ───────────────────────────────────────────────────────────
function TeachersTab({ teachers, onUpdate }) {
  const sorted = useMemo(() => {
    return [...teachers].sort((a, b) => (a.display_name || a.email).localeCompare(b.display_name || b.email));
  }, [teachers]);

  return (
    <Card className="border-[#E5E3DB]">
      <CardHeader>
        <CardTitle className="text-base text-[#1F2937] heading-font">All teachers ({sorted.length})</CardTitle>
        <p className="text-xs text-[#6B7280]">
          Your full teacher roster — auto-synced from <code className="text-[10px] bg-[#F3F1EC] px-1 py-0.5 rounded">student.teacher_email</code>. Override the display name or disable notifications per teacher.
        </p>
      </CardHeader>
      <CardContent>
        {sorted.length === 0 ? (
          <p className="text-xs text-[#6B7280] italic">No teachers yet — students need a `teacher_email` set.</p>
        ) : (
          <div className="space-y-2">
            {sorted.map((t) => (
              <TeacherRow key={t.email} teacher={t} onUpdate={onUpdate} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TeacherRow({ teacher, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [override, setOverride] = useState(teacher.display_name_override || "");
  const enabled = teacher.notification_enabled !== false;

  const save = async () => {
    await onUpdate(teacher.email, { display_name_override: override.trim() });
    setEditing(false);
  };

  return (
    <div
      data-testid={`teacher-row-${teacher.email}`}
      className="flex items-center justify-between gap-3 bg-white border border-[#E5E3DB] rounded-md px-3 py-2"
    >
      <div className="min-w-0 flex-1">
        {editing ? (
          <div className="flex items-center gap-2">
            <Input
              data-testid={`teacher-name-input-${teacher.email}`}
              value={override}
              onChange={(e) => setOverride(e.target.value)}
              placeholder={teacher.display_name_default || teacher.email}
              className="h-7 text-sm w-56"
              autoFocus
            />
            <Button size="sm" onClick={save} className="h-7 bg-[#1E3F33] text-white text-[11px]">
              <Check size={12} className="mr-1" /> Save
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setEditing(false); setOverride(teacher.display_name_override || ""); }} className="h-7 text-[11px]">
              <X size={12} />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-[#1F2937]">{teacher.display_name}</p>
            {teacher.display_name_override && (
              <Badge variant="outline" className="text-[9px] border-[#1E3F33]/30 text-[#1E3F33]">override</Badge>
            )}
            <button
              data-testid={`teacher-edit-name-${teacher.email}`}
              onClick={() => setEditing(true)}
              className="text-[10px] text-[#1E3F33] hover:underline"
            >
              edit
            </button>
          </div>
        )}
        <p className="text-[11px] text-[#6B7280] truncate">
          {teacher.email} · {teacher.student_count || 0} student{teacher.student_count === 1 ? "" : "s"}
          {teacher.last_synced_at && ` · synced ${new Date(teacher.last_synced_at).toLocaleDateString()}`}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Label className="text-[10px] uppercase tracking-wider text-[#6B7280]">Notify</Label>
        <Switch
          data-testid={`teacher-toggle-${teacher.email}`}
          checked={enabled}
          onCheckedChange={(v) => onUpdate(teacher.email, { notification_enabled: !!v })}
        />
      </div>
    </div>
  );
}


// ── Settings tab ───────────────────────────────────────────────────────────
function SettingsTab({ settings, onSave }) {
  const [enabled, setEnabled] = useState(settings?.enabled || false);
  const [senderName, setSenderName] = useState(settings?.sender_name || "");
  const [senderEmail, setSenderEmail] = useState(settings?.sender_email || "");
  const [subjectTemplate, setSubjectTemplate] = useState(settings?.default_subject_template || "");

  const save = () => onSave({
    enabled,
    sender_name: senderName,
    sender_email: senderEmail,
    default_subject_template: subjectTemplate,
  });

  return (
    <Card className="border-[#E5E3DB]">
      <CardHeader>
        <CardTitle className="text-base text-[#1F2937] heading-font">Notification settings</CardTitle>
        <p className="text-xs text-[#6B7280]">
          Mock-only send for now. These settings shape the email envelope so a real provider (Resend / SendGrid) can be wired in later without UI changes.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between bg-[#FAFAF7] border border-[#E5E3DB] rounded-md px-3 py-2.5">
          <div>
            <p className="text-sm font-medium text-[#1F2937]">Enable teacher notifications</p>
            <p className="text-[11px] text-[#6B7280]">When off, automated reminders won't fire. Manual previews and mock sends stay available for testing.</p>
          </div>
          <Switch
            data-testid="settings-enabled-toggle"
            checked={enabled}
            onCheckedChange={setEnabled}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] text-[#6B7280] uppercase tracking-wider">Sender display name</Label>
            <Input
              data-testid="settings-sender-name"
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              placeholder="Sarah Mitchell"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-[11px] text-[#6B7280] uppercase tracking-wider">Sender email (display)</Label>
            <Input
              data-testid="settings-sender-email"
              value={senderEmail}
              onChange={(e) => setSenderEmail(e.target.value)}
              placeholder="sarah.mitchell@frameed.io"
              className="mt-1"
            />
          </div>
        </div>

        <div>
          <Label className="text-[11px] text-[#6B7280] uppercase tracking-wider">Default subject template</Label>
          <Input
            data-testid="settings-subject-template"
            value={subjectTemplate}
            onChange={(e) => setSubjectTemplate(e.target.value)}
            placeholder="Tomorrow's session schedule from {provider_name}"
            className="mt-1"
          />
          <p className="text-[10px] text-[#6B7280] mt-1 italic">
            Available tokens: <code className="bg-[#F3F1EC] px-1 rounded">{"{provider_name}"}</code>, <code className="bg-[#F3F1EC] px-1 rounded">{"{teacher_name}"}</code>, <code className="bg-[#F3F1EC] px-1 rounded">{"{target_date_pretty}"}</code>, <code className="bg-[#F3F1EC] px-1 rounded">{"{target_date}"}</code>, <code className="bg-[#F3F1EC] px-1 rounded">{"{provider_role}"}</code>
          </p>
        </div>

        <div className="flex justify-end pt-2">
          <Button
            data-testid="settings-save"
            onClick={save}
            className="bg-[#1E3F33] hover:bg-[#173127] text-white"
          >
            Save settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


// ── History tab ────────────────────────────────────────────────────────────
const TYPE_LABELS = {
  tomorrow_reminder: "Tomorrow's reminder",
  no_session_week: "No-service heads-up",
  schedule_change_week: "Schedule changes",
  provider_unavailability: "Unavailability",
};
function typeLabel(t) {
  return TYPE_LABELS[t] || (t || "").replace(/_/g, " ");
}

function HistoryTab({ logs }) {
  const [openLog, setOpenLog] = useState(null);
  const [openHtml, setOpenHtml] = useState("");

  const openDetails = async (log) => {
    setOpenLog(log);
    try {
      const r = await api.getNotificationLog(log.id);
      setOpenHtml(r.data?.html_snapshot || "");
    } catch (e) {
      setOpenHtml("");
      toast.error("Failed to load log details");
    }
  };

  return (
    <>
      <Card className="border-[#E5E3DB]">
        <CardHeader>
          <CardTitle className="text-base text-[#1F2937] heading-font">Notification history</CardTitle>
          <p className="text-xs text-[#6B7280]">
            Every preview-and-send writes a log entry. Mock-only delivery for now — real provider deliveries will appear here too.
          </p>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-xs text-[#6B7280] italic">No notifications sent yet.</p>
          ) : (
            <div className="border border-[#E5E3DB] rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F9F8F6] text-[#6B7280]">
                  <tr>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold">Sent</th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold">Type</th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold">For</th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold">To</th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold">Status</th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold">Provider</th>
                    <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold"> </th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.id} data-testid={`log-row-${l.id}`} className="border-t border-[#ECEAE4] hover:bg-[#FAFAF7]">
                      <td className="px-3 py-2 text-[12px] text-[#6B7280] tabular-nums">{new Date(l.sent_at).toLocaleString()}</td>
                      <td className="px-3 py-2 text-[12px] text-[#1F2937]">{typeLabel(l.type)}</td>
                      <td className="px-3 py-2 text-[12px] text-[#6B7280]">{formatDatePretty(l.scheduled_for_date)}</td>
                      <td className="px-3 py-2 text-[12px] text-[#1F2937]">
                        <span className="font-medium">{l.teacher_name}</span>
                        <span className="text-[11px] text-[#6B7280] ml-1">&lt;{l.recipient_address}&gt;</span>
                      </td>
                      <td className="px-3 py-2">
                        <Badge className={
                          l.status === "sent"
                            ? "bg-[#E1EAE4] text-[#1E5A3F] hover:bg-[#E1EAE4]"
                            : l.status === "failed"
                              ? "bg-[#FBEFEC] text-[#B85C5C] hover:bg-[#FBEFEC]"
                              : "bg-[#FDF8F3] text-[#9C6B2E] hover:bg-[#FDF8F3]"
                        }>
                          {l.status}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-[11px] text-[#6B7280]">{l.delivery_provider || "mock"}</td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          data-testid={`log-view-${l.id}`}
                          variant="outline"
                          size="sm"
                          onClick={() => openDetails(l)}
                          className="h-7 text-[11px]"
                        >
                          <Eye size={12} className="mr-1" /> View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!openLog} onOpenChange={(o) => !o && setOpenLog(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-base">
              <ArrowClockwise size={14} className="inline mr-1" />
              {typeLabel(openLog?.type)} · {openLog?.teacher_name}
            </DialogTitle>
          </DialogHeader>
          {openLog && (
            <div className="space-y-3">
              <div className="grid grid-cols-[100px_1fr] gap-1 text-[12px]">
                <span className="text-[#6B7280] uppercase tracking-wider text-[10px]">To</span>
                <span className="text-[#1F2937]">{openLog.recipient_address}</span>
                <span className="text-[#6B7280] uppercase tracking-wider text-[10px]">Subject</span>
                <span className="text-[#1F2937]">{openLog.subject}</span>
                <span className="text-[#6B7280] uppercase tracking-wider text-[10px]">Sent</span>
                <span className="text-[#1F2937]">{new Date(openLog.sent_at).toLocaleString()}</span>
                <span className="text-[#6B7280] uppercase tracking-wider text-[10px]">Status</span>
                <span className="text-[#1F2937]">{openLog.status} · via {openLog.delivery_provider || "mock"}</span>
              </div>
              <div className="border border-[#E5E3DB] rounded-md overflow-hidden bg-[#F5F4EE]">
                {openHtml ? (
                  <iframe
                    title="Log preview"
                    srcDoc={openHtml}
                    sandbox=""
                    className="w-full h-[480px] bg-white"
                  />
                ) : (
                  <p className="p-4 text-xs text-[#6B7280] italic">No HTML snapshot available.</p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenLog(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ── Unavailable tab ────────────────────────────────────────────────────────
const REASONS = [
  { value: "sick", label: "Sick" },
  { value: "personal", label: "Personal" },
  { value: "pd", label: "Professional development" },
  { value: "meeting", label: "Meeting / conference" },
  { value: "other", label: "Other" },
];

function localDateISO(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function UnavailableTab({ provider, settings, onSettingsSaved, periods, onCommitted, onDeleted }) {
  const savedDefault = settings?.unavailability_default_message || "";
  const [reason, setReason] = useState("sick");
  const [startDate, setStartDate] = useState(localDateISO());
  const [endDate, setEndDate] = useState(localDateISO());
  // FP3-034D — Partial-day support. `unavailType="full"` keeps the
  // legacy date-range workflow; `unavailType="time"` uses a single
  // date + start_time + end_time for things like a 9-10 AM IEP
  // meeting or a 12-3 PM training.
  const [unavailType, setUnavailType] = useState("full");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  // FP3-034D — When reason="other" the provider chooses whether the
  // event is excused from fidelity. Sick/personal/pd are always
  // excused; meeting is always make-up-needed (with warning).
  const [otherExcused, setOtherExcused] = useState(false);
  const [note, setNote] = useState("");
  // Iter 33d Follow-up 14n — workflow-scoped saved message.
  // The textarea and a separate `savedMessage` track edit state vs the
  // version locked in for THIS workflow. Save → copy `message` →
  // `savedMessage`. Preview/Send always uses `savedMessage` so the
  // user sees that what they saved is what gets sent. The persisted
  // settings default still pre-fills both on first load.
  const [message, setMessage] = useState(savedDefault);
  const [savedMessage, setSavedMessage] = useState(savedDefault);
  const [saving, setSaving] = useState(false);
  const [notify, setNotify] = useState(true);
  const [preview, setPreview] = useState(null);
  const [building, setBuilding] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  // Pre-fill from persisted settings once on mount
  useEffect(() => {
    if (savedDefault && !message) {
      setMessage(savedDefault);
      setSavedMessage(savedDefault);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedDefault]);

  const messageDirty = (message || "").trim() !== (savedMessage || "").trim();

  // Save: lock the edited text in for THIS workflow. Also persist as
  // the next-time default so it pre-fills again.
  const saveMessage = async () => {
    if (!provider) return;
    setSaving(true);
    const next = (message || "").trim();
    try {
      const r = await api.updateNotificationSettings(
        { provider_id: provider.id },
        { unavailability_default_message: next }
      );
      onSettingsSaved?.(r.data);
      setSavedMessage(next);
      toast.success("Custom message saved", {
        description: "This message will be used for the preview and send below.",
      });
    } catch (e) {
      toast.error("Save failed", { description: e.message });
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    // FP3-034E — Cancel button must reset the ENTIRE draft, not just
    // the legacy three fields. Without this, switching between
    // partial-day and full-day or changing reason left stale time
    // windows / treatment overrides on the next preview.
    setReason("sick");
    const today = localDateISO();
    setStartDate(today);
    setEndDate(today);
    setUnavailType("full");
    setStartTime("09:00");
    setEndTime("10:00");
    setOtherExcused(false);
    setNote("");
    setMessage(savedDefault);
    setSavedMessage(savedDefault);
    setNotify(true);
    setPreview(null);
    setPreviewItem(null);
  };

  const buildPayload = (extra = {}) => {
    const p = {
      provider_id: provider.id,
      reason,
      start_date: startDate,
      end_date: unavailType === "time" ? startDate : endDate,
      note: note.trim(),
      message_to_teachers: savedMessage.trim(),
      all_day: unavailType === "full",
      ...extra,
    };
    if (unavailType === "time") {
      p.start_time = startTime;
      p.end_time = endTime;
    }
    if (reason === "other") {
      p.fidelity_treatment = otherExcused
        ? "excused_no_service"
        : "make_up_needed";
    }
    return p;
  };

  const generate = async () => {
    if (!provider) return;
    if (unavailType === "full" && endDate < startDate) {
      toast.error("End date must be on or after start date");
      return;
    }
    if (unavailType === "time") {
      if (!startTime || !endTime || endTime <= startTime) {
        toast.error("End time must be after start time");
        return;
      }
    }
    if (reason === "other" && otherExcused && !note.trim()) {
      toast.error("Internal note required", {
        description:
          "Please describe why this 'Other' event should be excused from fidelity.",
      });
      return;
    }
    if (messageDirty) {
      toast.error("Save your custom message first", {
        description: 'Click "Save" next to the message so the preview reflects what will be sent.',
      });
      return;
    }
    setBuilding(true);
    try {
      const r = await api.previewProviderUnavailability(buildPayload());
      setPreview(r.data);
      if (r.data.stats.teachers_count === 0) {
        toast.info("No teacher emails found for affected sessions", {
          description: r.data.stats.sessions_count
            ? `${r.data.stats.sessions_count} sessions in this range will be cancelled, but no students have a teacher_email set.`
            : "No sessions are scheduled in this range.",
        });
      }
    } catch (e) {
      toast.error("Preview failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setBuilding(false);
    }
  };

  const commit = async () => {
    if (!provider || !preview) return;
    setCommitting(true);
    try {
      const r = await api.commitProviderUnavailability(
        buildPayload({ notify, items: preview.items })
      );
      const sentLabel = notify
        ? `Cancelled ${r.data.affected_sessions} session${r.data.affected_sessions === 1 ? "" : "s"} · sent ${r.data.sent} email${r.data.sent === 1 ? "" : "s"}`
        : `Cancelled ${r.data.affected_sessions} session${r.data.affected_sessions === 1 ? "" : "s"} · no notifications sent`;
      toast.success(sentLabel, { description: "Mock-only send — logged to history." });
      setPreview(null);
      setNote("");
      // keep savedMessage for the next round; clear the textarea back to its saved value
      setMessage(savedMessage);
      await onCommitted();
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setCommitting(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.deleteProviderUnavailability(id);
      toast.success("Period removed · sessions un-cancelled");
      await onDeleted();
      setDeleteCandidate(null);
    } catch (e) {
      toast.error("Delete failed", { description: e.message });
    }
  };

  return (
    <div className="space-y-4">
      <Card className="border-[#E5E3DB]">
        <CardHeader>
          <CardTitle className="text-base text-[#1F2937] heading-font">Mark unavailable</CardTitle>
          <p className="text-xs text-[#6B7280]">
            Cancel sessions for a date range and notify affected teachers in one combined email per teacher. The internal reason is stored on the period; the teacher-facing message stays general unless you write a custom one below.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] text-[#6B7280] uppercase tracking-wider">Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="unavail-reason" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REASONS.map((r) => (
                    <SelectItem key={r.value} value={r.value} data-testid={`unavail-reason-${r.value}`}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-[11px] text-[#6B7280] uppercase tracking-wider">
                  {unavailType === "time" ? "Date" : "Start date"}
                </Label>
                <Input data-testid="unavail-start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1" />
              </div>
              {unavailType === "full" ? (
                <div className="flex-1">
                  <Label className="text-[11px] text-[#6B7280] uppercase tracking-wider">End date</Label>
                  <Input data-testid="unavail-end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1" />
                </div>
              ) : (
                <>
                  <div className="flex-1">
                    <Label className="text-[11px] text-[#6B7280] uppercase tracking-wider">Start time</Label>
                    <Input
                      data-testid="unavail-start-time"
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-[11px] text-[#6B7280] uppercase tracking-wider">End time</Label>
                    <Input
                      data-testid="unavail-end-time"
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* FP3-034D — Type selector. Full-day = legacy date-range
              behavior. Time block = single date with start/end time
              (e.g. doctor appointment 8-10:30 AM, IEP meeting
              9-10 AM). */}
          <div className="flex items-center gap-3">
            <Label className="text-[11px] text-[#6B7280] uppercase tracking-wider">
              Unavailable type
            </Label>
            <div
              className="inline-flex rounded-md border border-[#E5E3DB] overflow-hidden"
              role="radiogroup"
            >
              <button
                type="button"
                role="radio"
                aria-checked={unavailType === "full"}
                data-testid="unavail-type-full"
                onClick={() => setUnavailType("full")}
                className={`px-3 py-1.5 text-[11.5px] ${
                  unavailType === "full"
                    ? "bg-[#1E3F33] text-white"
                    : "bg-white text-[#3F4756] hover:bg-[#F5F4F0]"
                }`}
              >
                Full day
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={unavailType === "time"}
                data-testid="unavail-type-time"
                onClick={() => setUnavailType("time")}
                className={`px-3 py-1.5 text-[11.5px] border-l border-[#E5E3DB] ${
                  unavailType === "time"
                    ? "bg-[#1E3F33] text-white"
                    : "bg-white text-[#3F4756] hover:bg-[#F5F4F0]"
                }`}
              >
                Time block
              </button>
            </div>
          </div>

          {/* FP3-034D — Reason → fidelity warnings.
              meeting → make-up needed (always)
              other  → provider chooses; if excused, internal note required */}
          {reason === "meeting" && (
            <div
              data-testid="unavail-makeup-warning"
              className="rounded-md border border-[#E9D9A9] bg-[#FDF8F3] text-[#7A5B1F] p-3 text-[12px]"
            >
              <div className="text-[11px] uppercase tracking-[0.08em] font-semibold mb-1">
                May need make-up
              </div>
              Sessions during this time may need to be made up because
              this reason is not automatically excluded from service
              fidelity. Affected sessions will still be cancelled and
              teachers notified.
            </div>
          )}
          {reason === "other" && (
            <div
              data-testid="unavail-other-fidelity"
              className="rounded-md border border-[#E5E3DB] bg-[#FBFAF6] p-3 space-y-2"
            >
              <label className="flex items-start gap-2 text-[12px] text-[#1F2937] cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="unavail-other-excused"
                  checked={otherExcused}
                  onChange={(e) => setOtherExcused(e.target.checked)}
                  className="mt-0.5 accent-[#1E3F33] w-4 h-4"
                />
                <span>
                  <div className="font-semibold">
                    Exclude from fidelity (excused no-service event)
                  </div>
                  <div className="text-[11px] text-[#6B7280] mt-0.5">
                    If checked, an internal note is required so the
                    audit trail captures why this event was treated as
                    excused. If unchecked, missed minutes will surface
                    as needing make-up or fidelity attention.
                  </div>
                </span>
              </label>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] text-[#6B7280] uppercase tracking-wider">Internal note (not sent)</Label>
              <Input data-testid="unavail-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. State conference" className="mt-1" />
            </div>
            <div className="flex items-center gap-2 pt-5">
              <Switch
                data-testid="unavail-notify-toggle"
                checked={notify}
                onCheckedChange={setNotify}
                id="unavail-notify"
              />
              <Label htmlFor="unavail-notify" className="text-[12px] text-[#1F2937]">Notify affected teachers</Label>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-[11px] text-[#6B7280] uppercase tracking-wider">Custom teacher-facing message (optional)</Label>
              <div className="flex items-center gap-2">
                {!messageDirty && savedMessage && (
                  <span className="inline-flex items-center gap-1 text-[10px] text-[#1E5A3F] bg-[#E1EAE4] px-1.5 py-0.5 rounded font-semibold">
                    <Check size={10} weight="bold" /> Saved
                  </span>
                )}
                {messageDirty && (
                  <span className="text-[10px] text-[#9C6B2E] italic">unsaved changes</span>
                )}
                <Button
                  data-testid="unavail-message-save"
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={saveMessage}
                  disabled={saving}
                  className="h-6 text-[11px] border-[#E5E3DB] text-[#1E3F33] hover:bg-[#E8F0EB]"
                >
                  <FloppyDisk size={11} className="mr-1" /> {saving ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
            <Textarea
              data-testid="unavail-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={"Leave blank for the default: \"I'm out [dates]. Please don't send the students listed below for their scheduled sessions. Thank you!\""}
              rows={3}
              className="text-sm"
            />
            <p className="text-[10px] text-[#6B7280] mt-1 italic">
              Save locks this message in for the current workflow below and pre-fills it next time.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              data-testid="unavail-build-preview"
              onClick={generate}
              disabled={building}
              className="bg-[#9C6B2E] hover:bg-[#86571f] text-white"
            >
              <Eye size={14} className="mr-1.5" />
              {building ? "Building…" : "Preview affected sessions"}
            </Button>
            <Button
              data-testid="unavail-cancel"
              type="button"
              variant="outline"
              onClick={resetForm}
              className="border-[#E5E3DB] text-[#6B7280]"
            >
              <X size={14} className="mr-1.5" /> Cancel
            </Button>
          </div>

          {preview && (
            <div className="border border-[#E5E3DB] rounded-lg bg-[#FAFAF7] p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-semibold text-[#1F2937]">
                    {preview.stats.sessions_count} session{preview.stats.sessions_count === 1 ? "" : "s"} affected · {preview.stats.teachers_count} affected teacher{preview.stats.teachers_count === 1 ? "" : "s"} · {preview.stats.students_count} affected student{preview.stats.students_count === 1 ? "" : "s"}
                  </p>
                  <p className="text-[11px] text-[#6B7280]">
                    Range: <span className="font-medium">{preview.range_pretty}</span> · Reason: <span className="font-medium">{preview.reason_label}</span>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    data-testid="unavail-discard-preview"
                    type="button"
                    variant="outline"
                    onClick={() => setPreview(null)}
                    className="border-[#E5E3DB] text-[#6B7280]"
                    size="sm"
                  >
                    <X size={12} className="mr-1" /> Discard preview
                  </Button>
                  <Button
                    data-testid="unavail-commit"
                    onClick={commit}
                    disabled={committing || preview.stats.sessions_count === 0}
                    className="bg-[#1E3F33] hover:bg-[#173127] text-white"
                  >
                    <PaperPlaneTilt size={14} className="mr-1.5" />
                    {committing ? "Saving…" : (notify ? `Cancel sessions & notify (${preview.items.length})` : "Cancel sessions")}
                  </Button>
                </div>
              </div>
              <Separator className="bg-[#E5E3DB] my-3" />
              <div className="space-y-2">
                {preview.items.length === 0 ? (
                  <p className="text-[12px] text-[#6B7280] italic py-2">
                    {preview.stats.sessions_count
                      ? `${preview.stats.sessions_count} session${preview.stats.sessions_count === 1 ? "" : "s"} will be cancelled, but no affected students have a teacher_email set, so no email will be generated.`
                      : "No sessions are scheduled in that range."}
                  </p>
                ) : (
                  preview.items.map((it) => (
                    <div
                      key={it.teacher_email}
                      data-testid={`unavail-item-${it.teacher_email}`}
                      className="flex items-center justify-between gap-3 bg-white border border-[#E5E3DB] rounded-md px-3 py-2"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-[#1F2937] truncate">{it.teacher_name}</p>
                          {!it.notification_enabled && (
                            <Badge variant="outline" className="text-[9px] bg-[#FBEFEC] text-[#B85C5C] border-[#B85C5C]/30">disabled</Badge>
                          )}
                        </div>
                        <p className="text-[11px] text-[#6B7280] truncate">
                          {it.recipient_address} · {it.students_summary.length} affected session{it.students_summary.length === 1 ? "" : "s"}
                          {": " + it.students_summary.map((s) => `${s.student_name} (${s.date_pretty})`).slice(0, 4).join(", ")}
                        </p>
                      </div>
                      <Button
                        data-testid={`unavail-preview-${it.teacher_email}`}
                        variant="outline"
                        size="sm"
                        onClick={() => setPreviewItem(it)}
                        className="text-[11px] h-7 border-[#E5E3DB]"
                      >
                        <Eye size={12} className="mr-1" /> Preview
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-[#E5E3DB]">
        <CardHeader>
          <CardTitle className="text-base text-[#1F2937] heading-font">Active &amp; recent unavailability ({periods.length})</CardTitle>
          <p className="text-xs text-[#6B7280]">
            Removing a period un-cancels its sessions. Teacher notifications already sent stay in History as an audit trail.
          </p>
        </CardHeader>
        <CardContent>
          {periods.length === 0 ? (
            <p className="text-xs text-[#6B7280] italic">No unavailability periods recorded.</p>
          ) : (
            <div className="space-y-2">
              {periods.map((p) => (
                <div
                  key={p.id}
                  data-testid={`unavail-period-${p.id}`}
                  className="flex items-center justify-between gap-3 bg-white border border-[#E5E3DB] rounded-md px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#1F2937]">
                      {p.start_date} → {p.end_date} · <span className="text-[#9C6B2E]">{p.reason_label}</span>
                    </p>
                    <p className="text-[11px] text-[#6B7280]">
                      {p.sessions_affected_count} session{p.sessions_affected_count === 1 ? "" : "s"} cancelled · {p.teachers_notified_count} teacher{p.teachers_notified_count === 1 ? "" : "s"} notified
                      {p.note && ` · note: "${p.note}"`}
                    </p>
                  </div>
                  <Button
                    data-testid={`unavail-delete-${p.id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteCandidate(p)}
                    className="text-[11px] h-7 border-[#B85C5C]/30 text-[#B85C5C] hover:bg-[#FBEFEC]"
                  >
                    <Trash size={12} className="mr-1" /> Remove
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!previewItem} onOpenChange={(o) => !o && setPreviewItem(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="text-base">Email preview</DialogTitle></DialogHeader>
          {previewItem && (
            <div className="space-y-3">
              <div className="grid grid-cols-[80px_1fr] gap-1 text-[12px]">
                <span className="text-[#6B7280] uppercase tracking-wider text-[10px]">To</span>
                <span className="text-[#1F2937]">{previewItem.recipient_address}</span>
                <span className="text-[#6B7280] uppercase tracking-wider text-[10px]">Subject</span>
                <span className="text-[#1F2937] font-medium">{previewItem.subject}</span>
              </div>
              <div className="border border-[#E5E3DB] rounded-md overflow-hidden bg-[#F5F4EE]">
                <iframe data-testid="unavail-preview-iframe" title="Email preview" srcDoc={previewItem.html} sandbox="" className="w-full h-[480px] bg-white" />
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setPreviewItem(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteCandidate} onOpenChange={(o) => !o && setDeleteCandidate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this unavailability period?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteCandidate && `${deleteCandidate.start_date} → ${deleteCandidate.end_date} (${deleteCandidate.reason_label}).`} Cancelled sessions in this range will be restored to scheduled. Teacher emails already sent stay in History.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteCandidate && remove(deleteCandidate.id)}
              className="bg-[#B85C5C] hover:bg-[#9F4F4F] text-white"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
